
import React, { useState } from 'react';
import ResourcePageLayout from '@/components/layouts/ResourcePageLayout';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Mail, Phone, MapPin, Send } from 'lucide-react';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Form submission would go here
    console.log('Form submitted:', formData);
    // Reset form
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
    // Show success message
    alert('Thank you for your message! We will get back to you soon.');
  };
  
  return (
    <ResourcePageLayout 
      title="Contact Us" 
      description="We're here to help! Feel free to reach out with any questions or feedback."
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-Hormone Balance Guide-charcoal mb-1">
                Name
              </label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your name"
                required
                className="w-full"
              />
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-Hormone Balance Guide-charcoal mb-1">
                Email
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your.email@example.com"
                required
                className="w-full"
              />
            </div>
            
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-Hormone Balance Guide-charcoal mb-1">
                Subject
              </label>
              <Input
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="How can we help?"
                required
                className="w-full"
              />
            </div>
            
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-Hormone Balance Guide-charcoal mb-1">
                Message
              </label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Please provide details about your inquiry..."
                rows={5}
                required
                className="w-full"
              />
            </div>
            
            <Button type="submit" variant="coral" size="lg">
              <Send size={18} />
              Send Message
            </Button>
          </form>
        </div>
        
        <div className="space-y-6 bg-Hormone Balance Guide-beige/20 p-6 rounded-lg">
          <h3 className="text-xl font-semibold text-Hormone Balance Guide-charcoal">Other Ways to Connect</h3>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Mail className="text-Hormone Balance Guide-coral mt-1" size={20} />
              <div>
                <h4 className="font-medium text-Hormone Balance Guide-charcoal">Email Us</h4>
                <p className="text-Hormone Balance Guide-charcoal/80">hello@hormonebalanceguide.com</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Phone className="text-Hormone Balance Guide-coral mt-1" size={20} />
              <div>
                <h4 className="font-medium text-Hormone Balance Guide-charcoal">Call Us</h4>
                <p className="text-Hormone Balance Guide-charcoal/80">(555) 123-4567</p>
                <p className="text-sm text-Hormone Balance Guide-charcoal/60">Monday-Friday, 9am-5pm PST</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <MapPin className="text-Hormone Balance Guide-coral mt-1" size={20} />
              <div>
                <h4 className="font-medium text-Hormone Balance Guide-charcoal">Visit Us</h4>
                <p className="text-Hormone Balance Guide-charcoal/80">
                  123 Wellness Avenue<br />
                  San Francisco, CA 94110
                </p>
              </div>
            </div>
          </div>
          
          <div className="pt-4 border-t border-Hormone Balance Guide-beige/50">
            <h4 className="font-medium text-Hormone Balance Guide-charcoal mb-2">Follow Us</h4>
            <div className="flex gap-4">
              <a href="https://facebook.com/findhormony" target="_blank" rel="noopener noreferrer" className="text-Hormone Balance Guide-charcoal hover:text-Hormone Balance Guide-coral">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                </svg>
              </a>
              <a href="https://instagram.com/findhormony" target="_blank" rel="noopener noreferrer" className="text-Hormone Balance Guide-charcoal hover:text-Hormone Balance Guide-coral">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                </svg>
              </a>
              <a href="https://twitter.com/findhormony" target="_blank" rel="noopener noreferrer" className="text-Hormone Balance Guide-charcoal hover:text-Hormone Balance Guide-coral">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </ResourcePageLayout>
  );
};

export default ContactUs;
